/**
 * Import Service
 * API calls for CSV import, validation, and conflict resolution
 */

import axios from 'axios';
import config from '@config';
import authService from '../authServiceFactory';
import BaseApiService from '../base/BaseApiService';
import type {
  ConflictResolutionConfig,
  ImportHistoryRecord,
  ImportErrorRecord,
  ImportPreview,
  ExecuteImportResponse,
  ValidateImportBackendResponse,
  ScopeRulesResponse,
  RecalculateScopesResponse,
  ScopeRuleUpdatePayload,
  ImportHistoryResponse,
  ImportHistoryFilters,
  TriggerJobResponse,
  ScopeSettingsResponse,
  ScopeRecalculationRunResponse,
  ScopeRulesHistoryParams,
  ScopeRulesHistoryResponse,
  ImportType,
} from '@app-types/import';

const IMPORT_TEMPLATE_FILE_NAMES: Record<ImportType, string> = {
  contacts: 'contact_import_template.csv',
  contracts: 'contract_import_template.csv',
};

class ImportService extends BaseApiService {
  // ============================================================================
  // Conflict Resolution Config API
  // ============================================================================

  /**
   * Get the current conflict resolution configuration
   */
  async getConflictResolutionConfig(): Promise<ConflictResolutionConfig> {
    return this.get<ConflictResolutionConfig>('/import/config');
  }

  /**
   * Update the conflict resolution configuration
   */
  async updateConflictResolutionConfig(config: Partial<ConflictResolutionConfig>): Promise<ConflictResolutionConfig> {
    return this.put<ConflictResolutionConfig>('/import/config', config);
  }

  // ============================================================================
  // Import Validation API
  // ============================================================================

  /**
   * Validate a CSV file and get preview data.
   * Uses direct axios call to properly handle FormData with file uploads.
   */
  async validateImportFile(
    file: File,
    importType: string = 'contracts',
    importConfig?: Partial<ConflictResolutionConfig>,
  ): Promise<ValidateImportBackendResponse> {
    const formData = new FormData();
    formData.append('file', file);

    if (importConfig) {
      formData.append('config', JSON.stringify(importConfig));
    }

    const service = await authService;
    const token = await service.getAccessToken();

    const response = await axios.post(
      `${config.apiBaseUrl}/import/validate?importType=${encodeURIComponent(importType)}`,
      formData,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    return response.data;
  }

  // ============================================================================
  // Import Execution API
  // ============================================================================

  /**
   * Execute the import based on the preview.
   * Uses direct axios (no global timeout) because contact creation via Keycloak
   * can push the response time past the default apiTimeout for large imports.
   */
  async executeImport(
    preview: ImportPreview,
    rowOverrides?: Record<number, string>
  ): Promise<ExecuteImportResponse> {
    const service = await authService;
    const token = await service.getAccessToken();

    const response = await axios.post(
      `${config.apiBaseUrl}/import/execute`,
      { preview, overrides: rowOverrides || {} },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
    );
    return response.data;
  }

  /**
   * Cancel a running import
   */
  async cancelImport(importId: number): Promise<void> {
    return this.post<void>(`/import/${importId}/cancel`);
  }

  /**
   * Resume a failed import from its last checkpoint
   */
  async resumeImport(importId: number): Promise<ExecuteImportResponse> {
    return this.post<ExecuteImportResponse>(`/import/${importId}/resume`);
  }

  // ============================================================================
  // Import History API
  // ============================================================================

  /**
   * Get import history records with filtering, sorting, and pagination
   */
  async getImportHistory(params?: ImportHistoryFilters): Promise<ImportHistoryResponse> {
    return this.get<ImportHistoryResponse>('/import/history', params);
  }

  /**
   * Get a specific import history record
   */
  async getImportHistoryRecord(id: number): Promise<ImportHistoryRecord> {
    return this.get<ImportHistoryRecord>(`/import/history/${id}`);
  }

  /**
   * Get import errors for a specific import
   */
  async getImportErrors(id: number): Promise<ImportErrorRecord[]> {
    return this.get<ImportErrorRecord[]>(`/import/history/${id}/errors`);
  }

  /**
   * Set the is_completed flag on an import
   */
  async setImportCompleted(id: number, isCompleted: boolean): Promise<{ success: boolean }> {
    return this.put<{ success: boolean }>(`/import/history/${id}/completion`, { isCompleted });
  }

  /**
   * Download a subset CSV file containing only failed rows
   */
  async downloadSubsetFile(id: number): Promise<void> {
    const service = await authService;
    const token = await service.getAccessToken();

    const response = await axios.get(`${config.apiBaseUrl}/import/history/${id}/subset`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });

    const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subset_import_${id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Re-run an import with a corrected CSV file
   */
  async rerunImport(id: number, file: File): Promise<ExecuteImportResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const service = await authService;
    const token = await service.getAccessToken();

    const response = await axios.post(`${config.apiBaseUrl}/import/history/${id}/rerun`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response.data;
  }

  // ============================================================================
  // Scope Rules API
  // ============================================================================

  /**
   * Get scope rules
   */
  async getScopeRules(): Promise<ScopeRulesResponse> {
    return this.get<ScopeRulesResponse>('/scope-rules');
  }

  /**
   * Update scope rules
   */
  async updateScopeRules(rules: ScopeRuleUpdatePayload[]): Promise<ScopeRulesResponse> {
    return this.put<ScopeRulesResponse>('/scope-rules', { rules });
  }

  async getScopeSettings(): Promise<ScopeSettingsResponse> {
    return this.get<ScopeSettingsResponse>('/scope-rules/settings');
  }

  async updateScopeSettings(enabled: boolean): Promise<ScopeSettingsResponse> {
    return this.put<ScopeSettingsResponse>('/scope-rules/settings', {
      global_scope_calculation: enabled,
    });
  }

  async getLatestRecalculation(): Promise<ScopeRecalculationRunResponse | null> {
    return this.get<ScopeRecalculationRunResponse | null>('/scope-rules/recalculation');
  }

  async getScopeRulesHistory(params: ScopeRulesHistoryParams): Promise<ScopeRulesHistoryResponse> {
    return this.get<ScopeRulesHistoryResponse>('/scope-rules/history', params);
  }

  async recalculateScopes(): Promise<RecalculateScopesResponse> {
    return this.post<RecalculateScopesResponse>('/scope-rules/recalculate', {});
  }

  /**
   * Get import reference values by type (contract_type, category, etc.)
   */
  async getReferenceValues(type: string): Promise<string[]> {
    return this.get<string[]>('/import/reference-values', { type });
  }

  // ============================================================================
  // Scheduled Job Trigger
  // ============================================================================

  async triggerScheduledJob(jobName = 'Import Contracts'): Promise<TriggerJobResponse> {
    return this.post<TriggerJobResponse>('/jobs/trigger', { job_name: jobName });
  }

  // ============================================================================
  // Template Download
  // ============================================================================

  /**
   * Download the CSV import template (header row only) for the given import type
   */
  async downloadImportTemplate(importType: ImportType): Promise<void> {
    const service = await authService;
    const token = await service.getAccessToken();

    const response = await axios.get(
      `${config.apiBaseUrl}/import/template?importType=${encodeURIComponent(importType)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      },
    );

    const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = IMPORT_TEMPLATE_FILE_NAMES[importType];
    a.click();
    URL.revokeObjectURL(url);
  }
}

const importService = new ImportService();
export default importService;
